import axios from 'axios';
import { SubmissionError } from 'redux-form';
import shortid from 'shortid';

import { ASYNC_END, ASYNC_START, REQ_CANCELED } from './actionConstants';
import { logout } from './components/Auth/actionCreators';

import { getInterceptorAction } from './utils';

import { captureException } from './sentryInit';
import { history } from './store';

const isPromise = (v) => v && typeof v.then === 'function';

export const handleFormError = (err, forms = {}) => {
  const fieldErrors = {};
  const commonErrors = [];
  const error = err?.response?.data || err || {};

  const currentForm = Object.values(forms).find(({ submitting }) => submitting);
  const formFields = currentForm?.fields && Object.keys(currentForm.fields);

  const setError = (errorKey, value) => {
    // get dotIndex for cases like:
    // field: "allowedRoles.1", field: "allowedRoles.2"
    // ...to display all errors under the relevant fields

    const dotIndex = errorKey.indexOf('.');
    const key = dotIndex > 0 ? errorKey.substring(0, dotIndex) : errorKey;

    if (key === '_error') {
      commonErrors.push(value);
      return;
    }

    // to display a form error for a field there should be a corresponding key
    // or there should be '_error' to display an error message above the form
    // if there is an error for a non-existing field it's considered to be the '_error'

    if (fieldErrors[key]) {
      fieldErrors[key] = `${fieldErrors[key]} ${value}`;
    } else if (formFields?.includes(key)) {
      fieldErrors[key] = value;
    } else {
      setError('_error', value);
    }
  };

  const { data, msg } = error;

  if (!data && msg) setError('_error', msg);

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    setError(data.field, msg);

    data.field === 'nonFieldErrors' && setError('_error', msg);

    data.nonFieldErrors && setError('_error', data.nonFieldErrors.join('/n'));
  }

  if (data && typeof data === 'object' && Array.isArray(data)) {
    data.forEach((item) => setError(item.data.field, item.msg));
  }
  const errors = {
    _error: [...new Set(commonErrors)].join(' '), // set only unique errors
    ...fieldErrors,
  };

  throw new SubmissionError(errors);
};

const promiseMiddleware = (store) => (next) => (action) => {
  const interceptorAction = getInterceptorAction(action);

  if (isPromise(action.payload)) {
    const timestamp = shortid.generate();
    store.dispatch({
      type: ASYNC_START,
      subtype: action.type,
      id: timestamp,
      withoutLoading: action.withoutLoading || action.isTodayCount,
    });

    const newAction = { ...action };

    const onError = (err) => {
      newAction.error = true;
      newAction.payload = err?.response?.data;

      store.dispatch({
        type: ASYNC_END,
        promise: action.payload,
        id: timestamp,
      });

      if (axios.isCancel(err)) {
        store.dispatch({ type: REQ_CANCELED });
        return;
      }

      captureException(err);

      store.dispatch(newAction);

      if (action.isForm) {
        handleFormError(err, store.getState().form);
      }
    };

    return action.payload
      .then(
        (response) => {
          if (response instanceof Error) {
            return onError(response);
          }

          store.dispatch({
            type: ASYNC_END,
            correspondingAction: action.type,
            promise: action.payload,
            id: timestamp,
          });

          const data = response && response.data;

          if (getInterceptorAction({ res: data })) return null;

          newAction.payload = response;
          store.dispatch(newAction);
          return response;
        },
        (err) => onError(err),
      )
      .then(action.callback);
  }

  return interceptorAction ? next(interceptorAction) : next(action);
};

const errorHandler = (store, error) => {
  const errorCode = error?.code;
  const location = store?.getState()?.router?.location;
  switch (errorCode) {
    case 1548: // IP address is not in a whitelist. Permission Denied
    case 1200: // AuthenticationError
      if (location?.pathname === '/login') return;
      logout(store.dispatch);
      history.push('/login', { from: location });
      break;
    default:
      break;
  }
};

const localStorageMiddleware = (store) => (next) => (action) => {
  if (axios.isCancel(action?.err)) {
    store.dispatch({ type: REQ_CANCELED });
    return;
  }

  const error = action.error ? action.payload : action?.err?.response?.data;

  if (error) {
    errorHandler(store, error);
  }

  next(action);
};

export { promiseMiddleware, localStorageMiddleware };
